import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Pencil, PowerOff, Power, TicketPercent, Ticket, CheckCircle2, Clock, XCircle } from "lucide-react";
import { toast } from "sonner";
import dayjs from "dayjs";

const EMPTY = {
  name: "", description: "", discount_type: "nominal", discount_value: "",
  min_transaction: "", start_date: "", expiry_date: "", quota: "",
  max_per_customer: 1, eligibility: "all", outlet_id: "",
};

const fmtDisc = (c) => c.discount_type === "percentage" ? `${c.discount_value}%` : `Rp${Number(c.discount_value).toLocaleString("id-ID")}`;
const ELIG_LABEL = { all: "Semua", positive: "Puas", neutral: "Biasa Saja", negative: "Kurang Puas" };

const vBadge = (s) => s === "Active"
  ? <Badge className="bg-emerald-100 text-emerald-800 border-0">Aktif</Badge>
  : s === "Redeemed"
    ? <Badge className="bg-blue-100 text-blue-800 border-0">Ditukar</Badge>
    : <Badge className="bg-zinc-200 text-zinc-600 border-0">Kedaluwarsa</Badge>;

function CouponForm({ form, setForm, outlets, isSuper, editing }) {
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  return (
    <>
      <div className="space-y-1.5">
        <Label>Nama Kupon</Label>
        <Input required value={form.name} onChange={e => set("name", e.target.value)} data-testid="coupon-name-input" placeholder="Diskon Rp10.000" />
      </div>
      <div className="space-y-1.5">
        <Label>Deskripsi</Label>
        <Textarea rows={2} value={form.description} onChange={e => set("description", e.target.value)} data-testid="coupon-description-input" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Tipe Diskon</Label>
          <Select value={form.discount_type} onValueChange={v => set("discount_type", v)}>
            <SelectTrigger data-testid="coupon-discount-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="nominal">Nominal (Rp)</SelectItem>
              <SelectItem value="percentage">Persentase (%)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Nilai Diskon</Label>
          <Input type="number" min="1" required value={form.discount_value} onChange={e => set("discount_value", e.target.value)} data-testid="coupon-discount-value-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Min. Transaksi (Rp)</Label>
          <Input type="number" min="0" value={form.min_transaction} onChange={e => set("min_transaction", e.target.value)} data-testid="coupon-min-transaction-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Total Kuota</Label>
          <Input type="number" min="1" required value={form.quota} onChange={e => set("quota", e.target.value)} data-testid="coupon-quota-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Tanggal Mulai</Label>
          <Input type="date" required value={form.start_date} onChange={e => set("start_date", e.target.value)} data-testid="coupon-start-date-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Tanggal Berakhir</Label>
          <Input type="date" required value={form.expiry_date} onChange={e => set("expiry_date", e.target.value)} data-testid="coupon-expiry-date-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Maks. Voucher per Pelanggan</Label>
          <Input type="number" min="1" required value={form.max_per_customer} onChange={e => set("max_per_customer", e.target.value)} data-testid="coupon-max-per-customer-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Voucher untuk feedback</Label>
          <Select value={form.eligibility} onValueChange={v => set("eligibility", v)}>
            <SelectTrigger data-testid="coupon-eligibility-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              <SelectItem value="positive">Puas</SelectItem>
              <SelectItem value="neutral">Biasa Saja</SelectItem>
              <SelectItem value="negative">Kurang Puas</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {isSuper && !editing && (
        <div className="space-y-1.5">
          <Label>Outlet</Label>
          <Select value={form.outlet_id} onValueChange={v => set("outlet_id", v)}>
            <SelectTrigger data-testid="coupon-outlet-select"><SelectValue placeholder="Pilih outlet" /></SelectTrigger>
            <SelectContent>
              {outlets.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
    </>
  );
}

export default function Coupons() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [vFor, setVFor] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const [vLoading, setVLoading] = useState(false);

  const load = async () => {
    const [c, s] = await Promise.all([api.get("/coupons"), api.get("/coupon-stats")]);
    setItems(c.data); setStats(s.data);
    if (isSuper) {
      const o = await api.get("/outlets");
      setOutlets(o.data);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, outlet_id: isSuper ? "" : (user?.outlet_id || "") });
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name, description: c.description, discount_type: c.discount_type,
      discount_value: c.discount_value, min_transaction: c.min_transaction,
      start_date: c.start_date, expiry_date: c.expiry_date, quota: c.quota,
      max_per_customer: c.max_per_customer, eligibility: c.eligibility, outlet_id: c.outlet_id,
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = {
      ...form,
      discount_value: Number(form.discount_value),
      min_transaction: Number(form.min_transaction || 0),
      quota: Number(form.quota),
      max_per_customer: Number(form.max_per_customer),
    };
    try {
      if (editing) await api.patch(`/coupons/${editing.id}`, body);
      else await api.post("/coupons", body);
      toast.success(editing ? "Kupon diperbarui" : "Kupon dibuat");
      setOpen(false);
      await load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Gagal menyimpan kupon");
    } finally { setSaving(false); }
  };

  const toggle = async (c) => {
    try {
      await api.patch(`/coupons/${c.id}`, { is_active: !c.is_active });
      toast.success(c.is_active ? "Kupon dinonaktifkan" : "Kupon diaktifkan");
      await load();
    } catch {
      toast.error("Gagal mengubah status kupon");
    }
  };

  const showVouchers = async (c) => {
    setVFor(c); setVLoading(true); setVouchers([]);
    try {
      const { data } = await api.get(`/coupons/${c.id}/vouchers`);
      setVouchers(data);
    } catch { setVouchers([]); }
    finally { setVLoading(false); }
  };

  const statCards = stats ? [
    { icon: TicketPercent, label: "Total Kupon", value: stats.total_coupons, testid: "cstat-total" },
    { icon: Ticket, label: "Voucher Terbit", value: stats.issued, testid: "cstat-issued" },
    { icon: Clock, label: "Voucher Aktif", value: stats.active, testid: "cstat-active" },
    { icon: CheckCircle2, label: "Ditukar", value: stats.redeemed, testid: "cstat-redeemed" },
    { icon: XCircle, label: "Kedaluwarsa", value: stats.expired, testid: "cstat-expired" },
  ] : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Kupon & Voucher</h1>
          <p className="text-sm text-zinc-500 mt-1">Kelola promosi dan pantau penukaran voucher.</p>
        </div>
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={openCreate} data-testid="coupon-create-button">
          <Plus size={16} className="mr-2" /> Buat Kupon
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map(({ icon: Icon, label, value, testid }) => (
          <Card key={testid} className="border-zinc-200" data-testid={testid}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{label}</div>
                <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-700 grid place-items-center"><Icon size={14} /></div>
              </div>
              <div className="text-2xl font-extrabold mt-2" data-testid={`${testid}-value`}>{value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Kupon</th>
                {isSuper && <th className="text-left px-5 py-3 font-semibold">Outlet</th>}
                <th className="text-left px-5 py-3 font-semibold">Kuota</th>
                <th className="text-left px-5 py-3 font-semibold">Terbit</th>
                <th className="text-left px-5 py-3 font-semibold">Ditukar</th>
                <th className="text-left px-5 py-3 font-semibold">Aktif</th>
                <th className="text-left px-5 py-3 font-semibold">Kedaluwarsa</th>
                <th className="text-left px-5 py-3 font-semibold">Periode</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-left px-5 py-3 font-semibold">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={isSuper ? 10 : 9} className="px-5 py-8 text-center text-zinc-400">Belum ada kupon.</td></tr>}
              {items.map(c => (
                <tr key={c.id} className="border-t border-zinc-100 align-top" data-testid={`coupon-row-${c.id}`}>
                  <td className="px-5 py-3">
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-emerald-700 font-medium">{fmtDisc(c)}{c.min_transaction > 0 ? ` · Min Rp${Number(c.min_transaction).toLocaleString("id-ID")}` : ""}</div>
                    <div className="text-xs text-zinc-400">Untuk: {ELIG_LABEL[c.eligibility] || c.eligibility} · Maks {c.max_per_customer}/pelanggan</div>
                  </td>
                  {isSuper && <td className="px-5 py-3 text-zinc-600">{c.outlet_name || "—"}</td>}
                  <td className="px-5 py-3">{c.quota}</td>
                  <td className="px-5 py-3" data-testid={`coupon-issued-${c.id}`}>{c.issued}</td>
                  <td className="px-5 py-3">{c.redeemed}</td>
                  <td className="px-5 py-3">{c.active}</td>
                  <td className="px-5 py-3">{c.expired}</td>
                  <td className="px-5 py-3 text-zinc-500 whitespace-nowrap text-xs">{dayjs(c.start_date).format("DD MMM YY")} — {dayjs(c.expiry_date).format("DD MMM YY")}</td>
                  <td className="px-5 py-3">
                    {c.is_active
                      ? <Badge className="bg-emerald-100 text-emerald-800 border-0">Aktif</Badge>
                      : <Badge className="bg-zinc-200 text-zinc-600 border-0">Nonaktif</Badge>}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex gap-1.5 flex-wrap">
                      <Button variant="outline" size="sm" onClick={() => showVouchers(c)} data-testid={`coupon-vouchers-${c.id}`}>Voucher</Button>
                      <Button variant="outline" size="sm" onClick={() => openEdit(c)} data-testid={`coupon-edit-${c.id}`}><Pencil size={13} /></Button>
                      <Button variant="outline" size="sm"
                        className={c.is_active ? "text-red-700 border-red-300" : "text-emerald-700 border-emerald-300"}
                        onClick={() => toggle(c)} data-testid={`coupon-toggle-${c.id}`}>
                        {c.is_active ? <PowerOff size={13} /> : <Power size={13} />}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Kupon" : "Buat Kupon"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <CouponForm form={form} setForm={setForm} outlets={outlets} isSuper={isSuper} editing={editing} />
            <Button type="submit" disabled={saving || (isSuper && !editing && !form.outlet_id)} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="coupon-submit-button">
              {saving ? "Menyimpan..." : (editing ? "Simpan Perubahan" : "Buat Kupon")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!vFor} onOpenChange={(v) => { if (!v) setVFor(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Riwayat Voucher — {vFor?.name}</DialogTitle></DialogHeader>
          {vLoading && <div className="text-sm text-zinc-500 py-6 text-center">Memuat...</div>}
          {!vLoading && vouchers.length === 0 && <div className="text-sm text-zinc-400 py-6 text-center">Belum ada voucher.</div>}
          {!vLoading && vouchers.length > 0 && (
            <table className="w-full text-sm" data-testid="voucher-history-table">
              <thead className="text-xs uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="text-left py-2 pr-3 font-semibold">Kode</th>
                  <th className="text-left py-2 pr-3 font-semibold">Pelanggan</th>
                  <th className="text-left py-2 pr-3 font-semibold">Status</th>
                  <th className="text-left py-2 pr-3 font-semibold">Diambil</th>
                  <th className="text-left py-2 font-semibold">Ditukar</th>
                </tr>
              </thead>
              <tbody>
                {vouchers.map(v => (
                  <tr key={v.id} className="border-t border-zinc-100" data-testid={`voucher-row-${v.id}`}>
                    <td className="py-2 pr-3 font-mono font-semibold">{v.unique_code}</td>
                    <td className="py-2 pr-3 text-zinc-600">{v.customer_name || <span className="text-zinc-400">Anonim</span>}</td>
                    <td className="py-2 pr-3">{vBadge(v.status)}</td>
                    <td className="py-2 pr-3 text-zinc-500 whitespace-nowrap">{dayjs(v.issued_at).format("DD MMM YY HH:mm")}</td>
                    <td className="py-2 text-zinc-500 whitespace-nowrap">{v.redeemed_at ? dayjs(v.redeemed_at).format("DD MMM YY HH:mm") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
